import { create } from 'zustand'

import type {
  IngestNameConflict,
  NameConflictChoice,
} from '@/lib/upload/use-ingest-upload'

interface PendingPrompt {
  id: string
  conflict: IngestNameConflict
  resolve: (choice: NameConflictChoice) => void
}

interface NameConflictPromptState {
  /** Prompts waiting on the user, oldest first; the dialog shows `queue[0]`. */
  queue: PendingPrompt[]
  /** Number of mounted `DuplicateFileDialog`s able to answer prompts. */
  hosts: number
  registerHost: () => () => void
  answer: (id: string, choice: NameConflictChoice) => void
}

export const useNameConflictPromptStore = create<NameConflictPromptState>(
  (set, get) => ({
    queue: [],
    hosts: 0,
    registerHost: () => {
      set((state) => ({ hosts: state.hosts + 1 }))
      return () => {
        set((state) => ({ hosts: state.hosts - 1 }))
        // Last host gone: nobody can answer. Keep both — the same choice as
        // when no dialog was mounted, so an unmount never drops the upload.
        if (get().hosts === 0) {
          const pending = get().queue
          set({ queue: [] })
          for (const prompt of pending) prompt.resolve('keep_both')
        }
      }
    },
    answer: (id, choice) => {
      const prompt = get().queue.find((entry) => entry.id === id)
      if (!prompt) return
      set((state) => ({
        queue: state.queue.filter((entry) => entry.id !== id),
      }))
      prompt.resolve(choice)
    },
  }),
)

/**
 * Shows the "Duplicate file detected" dialog for `conflict` and resolves with
 * the user's choice. Concurrent uploads queue, one dialog at a time. With no
 * dialog mounted it keeps both files — the choice that never loses data.
 */
export function requestNameConflictChoice(
  conflict: IngestNameConflict,
): Promise<NameConflictChoice> {
  if (useNameConflictPromptStore.getState().hosts === 0) {
    return Promise.resolve('keep_both')
  }

  return new Promise((resolve) => {
    useNameConflictPromptStore.setState((state) => ({
      queue: [...state.queue, { id: crypto.randomUUID(), conflict, resolve }],
    }))
  })
}
